import { useEffect, useMemo, useRef, useState } from 'react'
import { useSession } from '@/context/AuthContext'
import {
  X, Plus, Loader2, Trash2, CalendarDays, Layers, GitBranch, User, FolderKanban, Hash,
} from 'lucide-react'
import type { ContentPlan, ContentStatus, FunnelChannel } from '@/lib/types'
import type { NewContentPlan } from '@/lib/useContentPlans'
import {
  CONTENT_STATUS_CONFIG, CONTENT_STATUS_ORDER, FUNNEL_CHANNELS, channelTint, formatPlanDate, toDateKey,
} from '@/lib/contentPlan'
import { isDirector, visibilityNeedsViewers, type Visibility } from '@/lib/roles'
import { VisibilityPicker } from '@/components/VisibilityPicker'
import { DEPARTMENTS } from '@/lib/departments'
import { useAllUsers } from '@/lib/useAllUsers'

const inputClass =
  'w-full px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl text-[#111110] text-sm font-body placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#111110] focus:bg-white transition-all'
const labelClass = 'flex items-center gap-1.5 text-[12px] font-semibold text-[#111110] font-body mb-1.5'

// ── Chip input (Content Bank & item per channel) ────────────
function TagInput({ tags, onChange, placeholder, accent = '#1A1916' }: {
  tags: string[]
  onChange: (next: string[]) => void
  placeholder: string
  accent?: string
}) {
  const [draft, setDraft] = useState('')
  const add = (raw: string) => {
    const v = raw.trim()
    if (!v) return
    if (!tags.includes(v)) onChange([...tags, v])
    setDraft('')
  }
  return (
    <div className="flex flex-wrap gap-1.5 px-2 py-2 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl focus-within:border-[#111110] focus-within:bg-white transition-all">
      {tags.map((t, i) => (
        <span key={`${t}-${i}`}
          className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-lg text-[11px] font-body font-medium bg-white border"
          style={{ borderColor: `${accent}33`, color: accent }}>
          {t}
          <button type="button" onClick={() => onChange(tags.filter((_, idx) => idx !== i))}
            className="p-0.5 rounded hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#DC2626] transition-colors">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft) }
          else if (e.key === 'Backspace' && !draft && tags.length) onChange(tags.slice(0, -1))
        }}
        onBlur={() => add(draft)}
        placeholder={tags.length ? '' : placeholder}
        className="flex-1 min-w-[100px] bg-transparent text-[12px] font-body text-[#111110] placeholder:text-[#C8C7BF] focus:outline-none px-1 py-0.5"
      />
    </div>
  )
}

// ── Multi-select PIC (chips + dropdown tambah) ──────────────
function PicMultiSelect({ options, selected, onChange, emptyHint }: {
  options: string[]
  selected: string[]
  onChange: (next: string[]) => void
  emptyHint: string
}) {
  const available = options.filter((o) => !selected.includes(o))
  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((name) => (
            <span key={name}
              className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg text-[11px] font-body font-medium bg-[#F1F1EF] border border-[#D6D5CF] text-[#1A1916]">
              {name}
              <button type="button" onClick={() => onChange(selected.filter((s) => s !== name))}
                aria-label={`Hapus ${name}`}
                className="p-0.5 rounded hover:bg-white text-[#AEADA8] hover:text-[#DC2626] transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <select value="" onChange={(e) => { if (e.target.value) onChange([...selected, e.target.value]) }}
        disabled={available.length === 0}
        className={`${inputClass} cursor-pointer disabled:opacity-60`}>
        <option value="">
          {available.length ? '+ Tambah PIC…' : selected.length ? 'Semua user terpilih' : emptyHint}
        </option>
        {available.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}

interface ProjectOption { id: string; name: string }

export function ContentPlanModal({ plan, defaultDate, onClose, onCreate, onUpdate, onDelete }: {
  plan: ContentPlan | null
  defaultDate?: string
  onClose: () => void
  onCreate: (data: NewContentPlan) => Promise<ContentPlan | null>
  onUpdate: (id: string, updates: Partial<NewContentPlan>) => Promise<void>
  onDelete?: (id: string) => Promise<void>
}) {
  const { data: session } = useSession()
  const isEdit = !!plan
  const today = useMemo(() => toDateKey(new Date()), [])

  const [title, setTitle] = useState(plan?.title ?? '')
  const [planDate, setPlanDate] = useState(plan?.planDate ?? defaultDate ?? today)
  const [status, setStatus] = useState<ContentStatus>(plan?.status ?? 'planned')
  const [contentBank, setContentBank] = useState<string[]>(plan?.contentBank ?? [])
  const [funnel, setFunnel] = useState<FunnelChannel[]>(plan?.funnel ?? [])
  const [notes, setNotes] = useState(plan?.notes ?? '')
  const { users: allUsers } = useAllUsers()
  // Pilih departemen bebas + PIC lintas BU = wewenang Director (mirror server).
  const isHolding = isDirector(session?.user?.role)
  const myDept = session?.user?.department ?? null
  const myUsername = session?.user?.username ?? null

  const [department, setDepartment] = useState<string | null>(plan?.department ?? myDept)
  const [pics, setPics] = useState<string[]>(plan?.pic ?? (myUsername ? [myUsername] : []))

  // Departemen acuan daftar PIC: HOLDING ikut selector; lainnya dept sendiri.
  const targetDept = isHolding ? department : myDept
  const picOptions = allUsers
    .filter((u) => u.username && u.department === targetDept)
    .map((u) => u.username)
    .sort((a, b) => a.localeCompare(b))

  const [projectId, setProjectId] = useState<string | null>(plan?.projectId ?? null)
  // Siapa yang boleh melihat konten ini. Picker hanya muncul untuk non-Member.
  const [visibility, setVisibility] = useState<Visibility>(plan?.visibility ?? 'public')
  const [viewerEmails, setViewerEmails] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [projects, setProjects] = useState<ProjectOption[]>([])
  const firstRef = useRef<HTMLInputElement>(null)

  useEffect(() => { firstRef.current?.focus() }, [])
  useEffect(() => {
    fetch('/api/projects').then((r) => (r.ok ? r.json() : [])).then((list) => {
      if (Array.isArray(list)) setProjects(list.map((p: ProjectOption) => ({ id: p.id, name: p.name })))
    }).catch(() => {})
  }, [])

  // Esc untuk tutup.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const addChannel = () => setFunnel((f) => [...f, { channel: '', items: [] }])
  const setChannel = (idx: number, patch: Partial<FunnelChannel>) =>
    setFunnel((f) => f.map((c, i) => (i === idx ? { ...c, ...patch } : c)))
  const removeChannel = (idx: number) => setFunnel((f) => f.filter((_, i) => i !== idx))

  const canSave =
    !!title.trim() && /^\d{4}-\d{2}-\d{2}$/.test(planDate)
    && (!visibilityNeedsViewers(visibility) || viewerEmails.length > 0)

  const handleSave = async () => {
    if (!canSave || saving) return
    setSaving(true)
    const cleanFunnel = funnel.map((c) => ({ channel: c.channel.trim(), items: c.items })).filter((c) => c.channel)
    const payload: NewContentPlan = {
      title: title.trim(), planDate, status, contentBank, funnel: cleanFunnel,
      notes: notes.trim(), pic: pics, projectId, department,
      // Visibility ditetapkan saat dibuat. Saat edit tidak dikirim supaya daftar
      // orang pilihan yang sudah tersimpan tidak ikut terhapus.
      ...(isEdit ? {} : { visibility, viewerEmails }),
    }
    try {
      if (isEdit && plan) {
        await onUpdate(plan.id, payload)
      } else {
        const created = await onCreate(payload)
        if (!created) { setSaving(false); return }
      }
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!plan || !onDelete || deleting) return
    setDeleting(true)
    try { await onDelete(plan.id); onClose() } finally { setDeleting(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />

      <div className="relative w-full max-w-[600px] bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${CONTENT_STATUS_CONFIG[status].accent}1A` }}>
              <CalendarDays className="w-4 h-4" style={{ color: CONTENT_STATUS_CONFIG[status].accent }} />
            </span>
            <div>
              <p className="font-display text-[15px] font-bold text-[#1A1916] leading-tight">
                {isEdit ? 'Edit Konten' : 'Tambah Konten'}
              </p>
              <p className="text-[11px] text-[#AEADA8] font-body leading-tight">{formatPlanDate(planDate)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className={labelClass}>Judul Konten <span className="text-[#DC2626] font-normal">*</span></label>
            <input ref={firstRef} value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="cth: Teaser Free Trial" className={inputClass} />
          </div>

          {/* Date + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}><CalendarDays className="w-3.5 h-3.5 text-[#AEADA8]" /> Tanggal</label>
              <input type="date" value={planDate} onChange={(e) => setPlanDate(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <div className="flex flex-wrap gap-1.5">
                {CONTENT_STATUS_ORDER.map((s) => {
                  const active = status === s
                  const c = CONTENT_STATUS_CONFIG[s]
                  return (
                    <button key={s} type="button" onClick={() => setStatus(s)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-body font-medium border transition-all ${active ? 'text-white' : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'}`}
                      style={active ? { backgroundColor: c.accent, borderColor: c.accent } : undefined}>
                      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : c.dot}`} />
                      {c.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Content Bank */}
          <div>
            <label className={labelClass}><Layers className="w-3.5 h-3.5 text-[#AEADA8]" /> Content Bank</label>
            <TagInput tags={contentBank} onChange={setContentBank} placeholder="cth: Video, Flyer, Wording — Enter untuk tambah" accent="#1A1916" />
          </div>

          {/* Funnel */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={`${labelClass} mb-0`}><GitBranch className="w-3.5 h-3.5 text-[#AEADA8]" /> Funnel / Distribusi</label>
              <button type="button" onClick={addChannel}
                className="flex items-center gap-1 text-[11px] font-body font-medium text-[#6366F1] hover:text-[#4338CA] transition-colors">
                <Plus className="w-3 h-3" /> Channel
              </button>
            </div>
            {funnel.length === 0 ? (
              <button type="button" onClick={addChannel}
                className="w-full py-3 rounded-xl border border-dashed border-[#D6D5CF] text-[12px] font-body text-[#AEADA8] hover:border-[#C8C7BF] hover:text-[#6B6860] transition-colors">
                + Tambah channel pertama (IG, WhatsApp, Reclub…)
              </button>
            ) : (
              <div className="space-y-2">
                {funnel.map((c, idx) => {
                  const t = channelTint(c.channel || 'channel')
                  return (
                    <div key={idx} className="rounded-xl border border-[#EEEDE8] p-2.5 bg-[#FAFAF8] animate-fade-in">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.text }} />
                        <input value={c.channel} onChange={(e) => setChannel(idx, { channel: e.target.value })}
                          list="content-funnel-channels" placeholder="Nama channel"
                          className="flex-1 bg-transparent text-[12.5px] font-body font-semibold text-[#1A1916] placeholder:text-[#C8C7BF] focus:outline-none" />
                        <button type="button" onClick={() => removeChannel(idx)}
                          className="p-1 rounded-lg text-[#C0BDB6] hover:text-[#DC2626] hover:bg-[#FEF2F2] transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <TagInput tags={c.items} onChange={(items) => setChannel(idx, { items })}
                        placeholder="cth: Reels, Story, Ads — Enter untuk tambah" accent={t.text} />
                    </div>
                  )
                })}
              </div>
            )}
            <datalist id="content-funnel-channels">
              {FUNNEL_CHANNELS.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>

          {/* PIC (multi-select user) */}
          <div>
            <label className={labelClass}><User className="w-3.5 h-3.5 text-[#AEADA8]" /> PIC</label>
            {isHolding && (
              <select value={department ?? ''}
                onChange={(e) => { setDepartment(e.target.value || null); setPics([]) }}
                className={`${inputClass} cursor-pointer mb-2`}>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
            <PicMultiSelect options={picOptions} selected={pics} onChange={setPics}
              emptyHint={picOptions.length ? '+ Tambah PIC…' : 'Belum ada user di departemen ini'} />
          </div>

          {/* Tautkan Project */}
          <div>
            <label className={labelClass}><FolderKanban className="w-3.5 h-3.5 text-[#AEADA8]" /> Tautkan Project</label>
            <select value={projectId ?? ''} onChange={(e) => setProjectId(e.target.value || null)} className={`${inputClass} cursor-pointer`}>
              <option value="">— Tidak ditautkan —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className={labelClass}><Hash className="w-3.5 h-3.5 text-[#AEADA8]" /> Catatan</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Konteks / arahan singkat (opsional)" className={`${inputClass} resize-none leading-relaxed`} />
          </div>

          {/* Siapa yang bisa melihat (hanya untuk non-Member, saat membuat baru) */}
          {!isEdit && (
            <VisibilityPicker value={visibility} onChange={setVisibility}
              viewerEmails={viewerEmails} onViewerEmailsChange={setViewerEmails} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F2F1EC] flex items-center justify-between flex-shrink-0">
          {isEdit && onDelete ? (
            <button onClick={handleDelete} disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-2 text-[13px] font-body text-[#DC2626] hover:bg-[#FEF2F2] rounded-xl transition-colors disabled:opacity-40">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Hapus
            </button>
          ) : <span />}
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm font-body text-[#8A8A85] hover:text-[#111110] transition-colors">Batal</button>
            <button onClick={handleSave} disabled={!canSave || saving}
              className="flex items-center gap-2 px-5 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {isEdit ? 'Simpan' : 'Tambah Konten'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
